import React, { useState } from 'react';

export default function AddMentorCheckout({ 
  currentPlan, 
  mentorCount, 
  onConfirm, 
  onCancel 
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Calculate pricing
  const planPricing = {
    'mentor-basic': { 1: 999, 2: 899, 3: 799, 4: 749, 5: 699, 6: 649, 7: 599, 8: 549, 9: 499, 10: 449 },
    'mentor-pro': { 1: 1299, 2: 1199, 3: 1099, 4: 1049, 5: 999, 6: 949, 7: 899, 8: 849, 9: 799, 10: 749 },
    'mentor-premium': { 1: 1599, 2: 1499, 3: 1399, 4: 1349, 5: 1299, 6: 1249, 7: 1199, 8: 1149, 9: 1099, 10: 1049 }
  };
  
  const currentSeatCount = currentPlan.totalSeats;
  const newTotalSeats = currentSeatCount + mentorCount;
  const planPrices = planPricing[currentPlan.planType] || planPricing['mentor-basic'];
  
  const currentPricePerSeat = planPrices[Math.min(currentSeatCount, 10)] || planPrices[10];
  const newPricePerSeat = planPrices[Math.min(newTotalSeats, 10)] || planPrices[10];
  
  const currentMonthlyTotal = currentPricePerSeat * currentSeatCount;
  const newMonthlyTotal = newPricePerSeat * newTotalSeats;
  const priceDifference = newMonthlyTotal - currentMonthlyTotal;
  
  const proratedAmount = Math.round((priceDifference * currentPlan.daysRemaining / 30) * 100) / 100;
  
  const handleConfirm = async () => {
    setIsProcessing(true);
    try {
      await onConfirm({
        proratedAmount,
        nextBillingCycle: newMonthlyTotal,
        currentSeats: currentSeatCount,
        newTotalSeats,
        mentorCount
      });
    } catch (error) {
      console.error('Error confirming mentor addition:', error);
    } finally {
      setIsProcessing(false);
    }
  };
  
  const steps = [
    {
      title: "Review Your Plan",
      description: "Current plan details and new mentor addition"
    },
    {
      title: "Billing Summary", 
      description: "Prorated charges and next billing cycle"
    },
    {
      title: "Confirmation",
      description: "Final review and payment processing"
    }
  ];
  
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Add Mentor to Your Plan</h2>
            <p className="text-gray-600">Professional step-by-step process to add {mentorCount} mentor seat{mentorCount > 1 ? 's' : ''}</p>
          </div>
          
          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-8">
            {steps.map((step, index) => (
              <div key={index} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                  currentStep > index + 1 
                    ? 'bg-green-500 border-green-500 text-white' 
                    : currentStep === index + 1
                    ? 'bg-blue-500 border-blue-500 text-white'
                    : 'border-gray-300 text-gray-400'
                }`}>
                  {currentStep > index + 1 ? '✓' : index + 1}
                </div>
                <div className="ml-3">
                  <div className={`text-sm font-medium ${
                    currentStep >= index + 1 ? 'text-gray-900' : 'text-gray-400'
                  }`}>
                    {step.title}
                  </div>
                  <div className="text-xs text-gray-500">{step.description}</div>
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-16 h-0.5 mx-4 ${
                    currentStep > index + 1 ? 'bg-green-500' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
          
          {/* Step Content */}
          <div className="bg-gray-50 rounded-xl p-6 mb-6">
            {currentStep === 1 && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Current Plan Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h4 className="font-semibold text-gray-800 mb-3">Current Plan</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Plan Type:</span>
                        <span className="font-medium">{currentPlan.planType?.replace('mentor-', '').toUpperCase()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Current Seats:</span>
                        <span className="font-medium">{currentSeatCount}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Monthly Cost:</span>
                        <span className="font-medium">₹{currentMonthlyTotal}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h4 className="font-semibold text-gray-800 mb-3">Adding Mentors</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Additional Seats:</span>
                        <span className="font-medium">{mentorCount}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">New Total Seats:</span>
                        <span className="font-medium">{newTotalSeats}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">New Monthly Cost:</span>
                        <span className="font-medium">₹{newMonthlyTotal}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {currentStep === 2 && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Billing Summary</h3>
                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3">Immediate Charges</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Prorated Amount:</span>
                          <span className="font-bold text-lg text-blue-600">₹{proratedAmount}</span>
                        </div>
                        <div className="text-xs text-gray-500">
                          Based on {currentPlan.daysRemaining} days remaining in current cycle
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-gray-800 mb-3">Next Billing Cycle</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Monthly Total:</span>
                          <span className="font-bold text-lg text-green-600">₹{newMonthlyTotal}</span>
                        </div>
                        <div className="text-xs text-gray-500">
                          For {newTotalSeats} mentor seats
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Important:</strong> You'll be charged ₹{proratedAmount} immediately for the additional {mentorCount} mentor seat{mentorCount > 1 ? 's' : ''}. 
                      Starting next billing cycle, you'll be charged ₹{newMonthlyTotal}/month for all {newTotalSeats} seats.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {currentStep === 3 && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Final Confirmation</h3>
                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Ready to Add Mentors</h4>
                    <p className="text-gray-600 mb-6">
                      You're about to add {mentorCount} mentor seat{mentorCount > 1 ? 's' : ''} to your {currentPlan.planType?.replace('mentor-', '').toUpperCase()} plan.
                    </p>
                    
                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-600">Immediate Charge:</span>
                        <span className="font-bold text-xl text-blue-600">₹{proratedAmount}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Next Billing Cycle:</span>
                        <span className="font-bold text-lg text-green-600">₹{newMonthlyTotal}/month</span>
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-500">
                      Click "Confirm & Pay" to process the payment and add the mentor seats to your account.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Navigation Buttons */}
          <div className="flex justify-between">
            <button
              onClick={onCancel}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            
            <div className="flex gap-3">
              {currentStep > 1 && (
                <button
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Previous
                </button>
              )}
              
              {currentStep < 3 ? (
                <button
                  onClick={() => setCurrentStep(currentStep + 1)}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Next Step
                </button>
              ) : (
                <button
                  onClick={handleConfirm}
                  disabled={isProcessing}
                  className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProcessing ? 'Processing...' : `Confirm & Pay ₹${proratedAmount}`}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}









